"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Chart hover state machine.
 *
 * - Desktop (mouse): pointerMove sets activeIndex; pointerLeave clears it.
 * - Touch: sticky mode — tap toggles activeIndex; tap outside the container clears.
 *   We auto-enable sticky when navigator.maxTouchPoints > 0 OR the most recent
 *   pointer event was pointerType="touch".
 */
export function useChartHover() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [sticky, setSticky] = useState<boolean>(false);

  useEffect(() => {
    if (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0) {
      setSticky(true);
    }
  }, []);

  // Outside-click dismiss (sticky/touch only).
  useEffect(() => {
    if (activeIndex === null || !sticky) return;
    function onDocPointerDown(event: PointerEvent) {
      const container = containerRef.current;
      if (!container) return;
      if (event.target instanceof Node && !container.contains(event.target)) {
        setActiveIndex(null);
      }
    }
    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [activeIndex, sticky]);

  const handlePointerMove = useCallback((index: number, pointerType: string) => {
    if (pointerType === "touch") {
      setSticky(true);
      return; // wait for pointerUp (tap) in sticky mode
    }
    setActiveIndex(index);
  }, []);

  const handlePointerLeave = useCallback(() => {
    if (sticky) return;
    setActiveIndex(null);
  }, [sticky]);

  const handlePointerUp = useCallback((index: number, pointerType: string) => {
    if (pointerType !== "touch") return;
    setActiveIndex((prev) => (prev === index ? null : index));
  }, []);

  return {
    containerRef,
    activeIndex,
    sticky,
    handlePointerMove,
    handlePointerLeave,
    handlePointerUp,
  };
}
